import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StaffHeader from '../components/StaffHeader';
import FeatureGate from '../components/FeatureGate';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { PRODUCT_TYPE } from '../config/product';
import { formatCurrency } from '../utils/formatCurrency';
import { companyDateRange } from '../utils/companyRange';
import usePaymentMethods from '../hooks/usePaymentMethods';
import { reportMethodLabel } from '../utils/paymentMethods';

const EXPENSE_CATEGORIES = [
  { value: 'helper_pay', label: 'Helper Pay' },
  { value: 'materials', label: 'Materials' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'repairs', label: 'Repairs' },
  { value: 'other', label: 'Other' },
];

const getCategoryLabel = (value) => {
  const cat = EXPENSE_CATEGORIES.find(c => c.value === value);
  return cat ? cat.label : value;
};

// Date range presets, as calendar days in the company's time zone
// (utils/companyRange). This Season is not here: it is the company's own season
// (Settings > Company > Season), which GET /api/company returns as `season`, computed by
// the server's services/season.js. Off-season that is the most recent season.
const getDateRange = (preset, timezone) =>
  (preset === 'this_month' || preset === 'last_month'
    ? companyDateRange(preset, timezone)
    : { start: '', end: '' });

function ReportsContent() {
  const { user } = useAuth();
  const timezone = user && user.timezone;

  const [loading, setLoading] = useState(true);
  const paymentMethods = usePaymentMethods();

  // Date range state
  const [datePreset, setDatePreset] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [presetError, setPresetError] = useState('');

  // Report data
  const [revenueData, setRevenueData] = useState({
    total: 0,
    paid: 0,
    unpaid: 0,
    byPaymentMethod: [],
  });
  const [expenseData, setExpenseData] = useState({
    total: 0,
    byCategory: [],
  });

  // Initialize dates on mount, and again once the company's zone is known
  useEffect(() => {
    const range = getDateRange('this_month', timezone);
    setStartDate(range.start);
    setEndDate(range.end);
  }, [timezone]);

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      params.append('product', PRODUCT_TYPE);

      const [revenueRes, expenseRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/reports/revenue?${params}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/expenses/summary?${params}`),
      ]);

      setRevenueData(revenueRes.data);
      setExpenseData({
        total: expenseRes.data.grandTotal || 0,
        byCategory: expenseRes.data.byCategory || [],
      });
    } catch (err) {
      console.error('Failed to load report data:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const { PullToRefreshIndicator } = usePullToRefresh(loadData);

  useEffect(() => {
    if (startDate && endDate) {
      loadData();
    }
  }, [loadData, startDate, endDate]);

  const handlePresetChange = async (preset) => {
    setDatePreset(preset);
    setPresetError('');
    if (preset === 'this_season') {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/company?product=${PRODUCT_TYPE}`);
        const season = response.data && response.data.season;
        if (!season) throw new Error('no season in the company response');
        setStartDate(season.startDate);
        setEndDate(season.endDate);
      } catch (err) {
        console.error('Failed to load the season:', err);
        setPresetError('Could not load your season. Choose Custom dates instead.');
      }
    } else if (preset !== 'custom') {
      const range = getDateRange(preset, timezone);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };

  // Calculate profit metrics
  const totalRevenue = revenueData.total || 0;
  const totalExpenses = expenseData.total || 0;
  const netProfit = totalRevenue - totalExpenses;
  const profitMargin = totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(1) : 0;

  return (
    <div className="reports-page">
      {/* Header */}
      <StaffHeader active="reports" />

      <PullToRefreshIndicator />

      <div className="page-container">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">Profit & Loss Report</h1>
          <p className="text-muted">Financial overview for your business</p>
        </div>

        {/* Date Range Filters */}
        <div className="filters-section">
          <div className="preset-buttons">
            <button
              className={`preset-btn ${datePreset === 'this_month' ? 'active' : ''}`}
              onClick={() => handlePresetChange('this_month')}
            >
              This Month
            </button>
            <button
              className={`preset-btn ${datePreset === 'last_month' ? 'active' : ''}`}
              onClick={() => handlePresetChange('last_month')}
            >
              Last Month
            </button>
            <button
              className={`preset-btn ${datePreset === 'this_season' ? 'active' : ''}`}
              onClick={() => handlePresetChange('this_season')}
            >
              This Season
            </button>
            <button
              className={`preset-btn ${datePreset === 'custom' ? 'active' : ''}`}
              onClick={() => handlePresetChange('custom')}
            >
              Custom
            </button>
          </div>

          {presetError && <div className="alert alert-error" aria-live="polite">{presetError}</div>}

          {datePreset === 'custom' && (
            <div className="custom-date-range">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : totalRevenue === 0 && totalExpenses === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.5 }}>📊</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--color-text)' }}>Not enough data for reports</h3>
            <p style={{ color: 'var(--color-text-light)', margin: 0 }}>Complete some routes and add expenses to generate reports</p>
          </div>
        ) : (
          <>
            {/* Profit Summary Cards */}
            <div className="profit-summary">
              <div className="summary-card revenue">
                <div className="summary-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-label">Total Revenue</div>
                  <div className="summary-value">{formatCurrency(totalRevenue)}</div>
                </div>
              </div>
              <div className="summary-card expenses">
                <div className="summary-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                    <path d="M1 10h22" />
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-label">Total Expenses</div>
                  <div className="summary-value">{formatCurrency(totalExpenses)}</div>
                </div>
              </div>
              <div className={`summary-card profit ${netProfit >= 0 ? 'positive' : 'negative'}`}>
                <div className="summary-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-label">Net Profit</div>
                  <div className="summary-value">{formatCurrency(netProfit)}</div>
                  <div className="summary-subtext">{profitMargin}% margin</div>
                </div>
              </div>
            </div>

            {/* Revenue Section */}
            <div className="report-section">
              <h2 className="section-title">Revenue Breakdown</h2>
              {/* Stops completed before the storm depth was entered have no
                  figure yet: they are left out of every amount above and
                  below, and counted here. */}
              {revenueData.pendingDepthCount > 0 && (
                <p className="awaiting-depth-note" role="note">
                  Awaiting depth: {revenueData.pendingDepthCount} stop{revenueData.pendingDepthCount !== 1 ? 's' : ''}.
                  {' '}Not included in these totals until the storm depth is entered.
                </p>
              )}

              <div className="breakdown-cards">
                <div className="breakdown-card">
                  <div className="breakdown-header">
                    <span className="breakdown-label">By Payment Status</span>
                  </div>
                  <div className="breakdown-items">
                    <div className="breakdown-item">
                      <span className="item-label">
                        <span className="status-dot paid"></span>
                        Paid
                      </span>
                      <span className="item-value">{formatCurrency(revenueData.paid)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span className="item-label">
                        <span className="status-dot unpaid"></span>
                        Unpaid
                      </span>
                      <span className="item-value">{formatCurrency(revenueData.unpaid)}</span>
                    </div>
                  </div>
                </div>

                <div className="breakdown-card">
                  <div className="breakdown-header">
                    <span className="breakdown-label">By Payment Method</span>
                  </div>
                  <div className="breakdown-items">
                    {revenueData.byPaymentMethod && revenueData.byPaymentMethod.length > 0 ? (
                      revenueData.byPaymentMethod.map((method, idx) => (
                        <div key={idx} className="breakdown-item">
                          <span className="item-label">{reportMethodLabel(paymentMethods, method.payment_method)}</span>
                          <span className="item-value">{formatCurrency(method.total)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="breakdown-empty">No payment data</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Expenses Section */}
            <div className="report-section">
              <h2 className="section-title">Expense Breakdown</h2>

              <div className="breakdown-cards">
                <div className="breakdown-card full-width">
                  <div className="breakdown-header">
                    <span className="breakdown-label">By Category</span>
                    <span className="breakdown-total">{formatCurrency(totalExpenses)}</span>
                  </div>
                  <div className="breakdown-items">
                    {expenseData.byCategory && expenseData.byCategory.length > 0 ? (
                      expenseData.byCategory.map((cat, idx) => (
                        <div key={idx} className="breakdown-item">
                          <span className="item-label">
                            <span className={`category-badge ${cat.category}`}>
                              {getCategoryLabel(cat.category)}
                            </span>
                          </span>
                          <span className="item-value">{formatCurrency(cat.total)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="breakdown-empty">No expenses recorded</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .reports-page {
          min-height: 100vh;
          background: var(--color-background);
        }

        .page-header {
          margin-bottom: 24px;
        }

        .filters-section {
          margin-bottom: 24px;
        }

        .preset-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }

        .preset-btn {
          padding: 10px 16px;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          color: var(--color-text);
        }

        .preset-btn:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }

        .preset-btn.active {
          background: var(--color-primary);
          border-color: var(--color-primary);
          color: white;
        }

        .custom-date-range {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .date-input-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .date-input-group label {
          font-size: 0.85rem;
          color: var(--color-text-light);
        }

        .date-input-group .form-input {
          width: 160px;
        }

        /* Profit Summary Cards */
        .profit-summary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }

        .summary-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .summary-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .summary-icon svg {
          width: 24px;
          height: 24px;
        }

        .summary-card.revenue .summary-icon {
          background: #dcfce7;
          color: #16a34a;
        }

        .summary-card.expenses .summary-icon {
          background: #fee2e2;
          color: #dc2626;
        }

        .summary-card.profit .summary-icon {
          background: #dbeafe;
          color: #2563eb;
        }

        .summary-card.profit.positive .summary-icon {
          background: #dcfce7;
          color: #16a34a;
        }

        .summary-card.profit.negative .summary-icon {
          background: #fee2e2;
          color: #dc2626;
        }

        .summary-content {
          flex: 1;
          min-width: 0;
        }

        .summary-label {
          font-size: 0.85rem;
          color: var(--color-text-light);
          margin-bottom: 4px;
        }

        .summary-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .summary-subtext {
          font-size: 0.8rem;
          color: var(--color-text-light);
          margin-top: 2px;
        }

        /* Report Sections */
        .report-section {
          margin-bottom: 32px;
        }

        .section-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--color-text);
          margin-bottom: 16px;
        }

        .breakdown-cards {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .breakdown-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .breakdown-card.full-width {
          grid-column: 1 / -1;
        }

        .breakdown-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f0f0f0;
        }

        .breakdown-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-text);
        }

        .breakdown-total {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-primary);
        }

        .breakdown-items {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .breakdown-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .item-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          color: var(--color-text);
        }

        .item-value {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-text);
        }

        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        .status-dot.paid {
          background: #16a34a;
        }

        .status-dot.unpaid {
          background: #dc2626;
        }

        .awaiting-depth-note {
          margin: 0 0 12px;
          padding: 10px 12px;
          border: 1px solid #f59e0b;
          background: #fffbeb;
          color: #92400e;
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .breakdown-empty {
          font-size: 0.9rem;
          color: var(--color-text-light);
          text-align: center;
          padding: 12px;
        }

        .category-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }

        .category-badge.helper_pay { background: #dbeafe; color: #1e40af; }
        .category-badge.materials { background: #fef3c7; color: #92400e; }
        .category-badge.fuel { background: #fee2e2; color: #991b1b; }
        .category-badge.equipment { background: #e0e7ff; color: #3730a3; }
        .category-badge.repairs { background: #fce7f3; color: #9d174d; }
        .category-badge.other { background: #e5e7eb; color: #374151; }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .profit-summary {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .summary-card {
            padding: 16px;
          }

          .summary-value {
            font-size: 1.35rem;
          }

          .breakdown-cards {
            grid-template-columns: 1fr;
          }

          .preset-buttons {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }

          .preset-btn {
            text-align: center;
          }

          .custom-date-range {
            flex-direction: column;
          }

          .date-input-group .form-input {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .summary-icon {
            width: 40px;
            height: 40px;
          }

          .summary-icon svg {
            width: 20px;
            height: 20px;
          }

          .summary-value {
            font-size: 1.25rem;
          }
        }

        /* Nav styles */
        .app-header-content {
          overflow: hidden;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 1;
          overflow: hidden;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }
        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}

// Wrap the content in FeatureGate
function Reports() {
  return (
    <FeatureGate feature="reports" lapsedMayRead>
      <ReportsContent />
    </FeatureGate>
  );
}

export default Reports;
